import { createConnectQueryKey, MethodUnaryDescriptor, useQuery, useTransport } from '@connectrpc/connect-query'
import { Message, AnyMessage, PartialMessage, ServiceType, MethodInfo } from '@bufbuild/protobuf'
import { useMemo, useRef } from 'react'
import { useQueries } from '@tanstack/react-query'
import { useLoggingMemo } from '../../utils'

export const useBaseTransport = () => {
  const transport = useTransport()
  return useMemo(() => {
    return {
      unary: <I extends Message<I> = AnyMessage, O extends Message<O> = AnyMessage>(
        service: ServiceType,
        method: MethodInfo<I, O>,
        input: PartialMessage<I>,
        timeoutMs?: number,
      ) => transport.unary(service, method, undefined, timeoutMs, undefined, input),
      stream: <I extends Message<I> = AnyMessage, O extends Message<O> = AnyMessage>(
        service: ServiceType,
        method: MethodInfo<I, O>,
        input: AsyncIterable<PartialMessage<I>>,
        timeoutMs?: number,
      ) => transport.stream(service, method, undefined, timeoutMs, undefined, input),
    }
  }, [transport])
}

export const useBaseQueries = <I extends Message<I> = AnyMessage, O extends Message<O> = AnyMessage>(
  service: ServiceType,
  method: MethodInfo<I, O> & Pick<MethodUnaryDescriptor<I, O>, 'I' | 'name' | 'service'>,
  inputs: PartialMessage<I>[],
) => {
  const transport = useBaseTransport()
  return useQueries({
    queries: inputs.map((input) => ({
      queryKey: createConnectQueryKey(method, input),
      queryFn: async () => transport.unary(service, method, input),
    })),
  })
}

export const useBaseQueryStream = <I extends Message<I> = AnyMessage, O extends Message<O> = AnyMessage>(
  service: ServiceType,
  method: MethodInfo<I, O>,
  input: AsyncIterable<PartialMessage<I>>,
) => {
  const transport = useBaseTransport()
  return transport.stream(service, method, input)
}

export const useMemoizedQuery: typeof useQuery = (methodSig, input, options) => {
  const query = useQuery(methodSig, input, options)
  return useLoggingMemo(
    () => query,
    [query.data, query.error, query.isError, query.isPending, query.isLoading, query.isLoadingError, query.isRefetchError, query.isSuccess, query.status], // eslint-disable-line react-hooks/exhaustive-deps
    'useMemoizedQuery',
  )
}

export const useMemoizedQueries: typeof useBaseQueries = (service, method, inputs) => {
  const queries = useBaseQueries(service, method, inputs)
  const queriesRef = useRef(queries)

  const dataChanged =
    queriesRef.current.length !== queries.length ||
    (queries.length > 0 &&
      queries.some((query, i) => {
        const prevQuery = queriesRef.current[i]
        if (!prevQuery) {
          return true
        }
        return (
          query.data !== prevQuery.data ||
          query.error !== prevQuery.error ||
          query.isError !== prevQuery.isError ||
          query.isPending !== prevQuery.isPending ||
          query.isLoading !== prevQuery.isLoading ||
          query.isLoadingError !== prevQuery.isLoadingError ||
          query.isRefetchError !== prevQuery.isRefetchError ||
          query.isSuccess !== prevQuery.isSuccess ||
          query.status !== prevQuery.status
        )
      }))
  if (dataChanged) {
    if (method.name === 'GetCompanyNikkeiNeedsIndustrySmallAssignmentsByNikkeiNeedsIndustrySmallId') {
      console.log('[useMemoizedQueries] data changed', queries, queriesRef.current)
    }
    queriesRef.current = queries
  }
  return useLoggingMemo(() => queriesRef.current, [queriesRef.current], 'useMemoizedQueries') // eslint-disable-line react-hooks/exhaustive-deps
}
